import {
	DownloadOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	EyeOutlined,
	PlusOutlined,
} from '@ant-design/icons'
import type { Member, MemberAttribute } from '@di-devs/member-feature-plugin/shared'
import { useBetterFeatureClient } from '@di-devs/utils-fe'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	Badge,
	Button,
	Card,
	Col,
	DatePicker,
	Divider,
	Form,
	Image,
	Input,
	Modal,
	message,
	Row,
	Table,
	Tooltip,
} from 'antd'
import { motion } from 'framer-motion'
import fileDownload from 'js-file-download'
import moment from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMatch, useNavigate, useSearchParams } from 'react-router'
import {
	BaseAnimationComponent,
	ChatModalComponent,
	MemberDetailModalComponent,
	MemberEditDetailModalComponent,
	MemberNoteModalComponent,
	PageHeaderComponent,
	TapAnimationComponent,
} from '@/components'
import MemberVisit from '@/components/admin/Member/MemberVisit'
import type { FeatureClient } from '@/config/client'
import { useSSEEvent } from '@/context/SSEContext'
import { useListMemberAttributes } from '@/queries/memberAttributes.queries'
import { API, COMMONS } from '@/utils'
import MembersFilters from './components/MembersFilters'

const MotionColComponent = motion(Col)

const Members = (props: Props) => {
	const { publicSettings } = props

	const queryClient = useQueryClient()
	const reactFeatureClient = useBetterFeatureClient<FeatureClient>()
	const [searchParams] = useSearchParams()
	const navigate = useNavigate()
	const isCampaignPage = useMatch(COMMONS.ADMIN_CAMPAIGN_MEMBERS_ROUTE)

	const memberId = searchParams.get('memberId')

	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF()
	const [membersCSVForm] = Form.useForm()
	const [memberVisitsCSVForm] = Form.useForm()
	const membersRef = useRef<any>()

	const [currentMemberId, setCurrentMemberId] = useState<number | null>(null)

	const [isMemberRemarksModalVisible, setIsMemberRemarksModalVisible] = useState(false)
	const [isMemberDetailModalVisible, setIsMemberDetailModalVisible] = useState(false)
	const [isMemberEditDetailModalVisible, setIsMemberEditDetailModalVisible] = useState(false)
	const [isChatModalVisible, setIsChatModalVisible] = useState(false)

	const [paginationPerPage, setPaginationPerPage] = useState(20)
	const [paginationPage, setPaginationPage] = useState(1)
	const [paginationTotal, setPaginationTotal] = useState(0)
	const [paginationSort, setPaginationSort] = useState('desc')
	const [paginationSortKey, setPaginationSortKey] = useState('updatedAt')

	const [filters, setFilters] = useState<any>(COMMONS.DEFAULT_MEMBERS_FILTERS_STATE)

	const [modal, contextHolder] = Modal.useModal()

	const memberAttributesQuery = useListMemberAttributes(reactFeatureClient)
	const memberAttributes: MemberAttribute[] =
		memberAttributesQuery.data?.data?.data?.filter((item: any) => item?.isAdminDisplayed) || []

	// Use Better Feature client for member listing with mutation pattern
	const membersQuery = useQuery({
		queryKey: [
			API.QUERY_KEY_ADMIN_MEMBERS,
			{
				paginationPerPage: paginationPerPage,
				paginationPage: paginationPage,
				paginationSort: paginationSort,
				paginationSortKey: paginationSortKey,
				filters,
			},
		],
		queryFn: async () =>
			await API.ADMIN_GET_MEMBERS({
				paginationPerPage,
				paginationPage,
				paginationSort,
				paginationSortKey,
				filterIsCampaignPage: isCampaignPage,
				filters,
			}),
		placeholderData: keepPreviousData,
	})

	useEffect(() => {
		if (membersQuery.isSuccess && membersQuery.data) {
			const response: FetchResponse = membersQuery.data
			if (isMountedRef.current) {
				membersRef.current = response.data?.rows || []
				setPaginationPerPage(response.data?.pp || 20)
				setPaginationPage(response.data?.p || 1)
				setPaginationTotal(response.data?.count || 0)
				setPaginationSort(response.data?.sort || 'desc')
				setPaginationSortKey(response.data?.sortKey || 'updatedAt')
			}
		}
	}, [membersQuery.isSuccess, membersQuery.data])

	// Hook for member detail using enhanced API endpoint for comprehensive data
	const memberDetailQuery = useQuery({
		enabled: !!currentMemberId,
		queryFn: async () => await API.ADMIN_GET_MEMBER_DETAIL({ memberId: currentMemberId }),
		queryKey: [API.QUERY_KEY_ADMIN_MEMBER_DETAIL, currentMemberId],
	})

	const currentMember = memberDetailQuery.data?.data

	const memberDeleteMutation = useMutation({
		mutationFn: async (data: { memberId: string }) =>
			await reactFeatureClient.deleteMember(data.memberId),
		onSuccess: () => {
			message.success(COMMONS.SUCCESS_DELETE_MSG)
			queryClient.invalidateQueries({
				queryKey: [API.QUERY_KEY_ADMIN_MEMBERS],
			})
		},
	})

	const membersExportMutation = useMutation({
		mutationFn: async (data: any) => {
			const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
			return await API.generateMemberCSV({
				...data,
				timeZone: clientTimezone,
			})
		},
		onSuccess: (response: any) => {
			if (isMountedRef.current) {
				if (response?.data || response) {
					const csvData = response?.body || response?.data || response
					fileDownload(
						'\uFEFF' + csvData,
						`${moment().format('YYYYMMDDHHmm')}_お客様リスト.csv`,
						'text/csv',
					)
				}
			}
		},
		onError: (error: any) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				message.warning(COMMONS.WARN_PASSWORD_NOT_MATCH_MSG)
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning(COMMONS.ERROR_SESSION_MSG)
				navigate(COMMONS.ADMIN_LOGIN_ROUTE)
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			} else {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			}
		},
	})

	const memberVisitsExportMutation = useMutation({
		mutationFn: async (data: { from: string; to: string; password: string }) =>
			await API.generateMemberVisitCSV(data),
		onSuccess: (response: any) => {
			if (isMountedRef.current) {
				if (response?.data || response) {
					const csvData = response?.body || response?.data || response
					fileDownload(
						'\uFEFF' + csvData,
						`${moment().format('YYYYMMDDHHmm')}_来館履歴.csv`,
						'text/csv',
					)
				}
			}
		},
		onError: (error: any) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				message.warning(COMMONS.WARN_PASSWORD_NOT_MATCH_MSG)
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning(COMMONS.ERROR_SESSION_MSG)
				navigate(COMMONS.ADMIN_LOGIN_ROUTE)
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			} else {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			}
		},
	})

	useEffect(() => {
		setFilters({
			...filters,
			memberId,
		})
		queryClient.invalidateQueries({ queryKey: [API.QUERY_KEY_ADMIN_MEMBERS] })
	}, [memberId])

	const members = membersQuery.data?.data?.rows ?? []

	const memberHandler = useCallback(
		(response: { memberId?: number | string | null; data?: any }) => {
			if (response?.memberId) {
				const targetMemberId = String(response.memberId)

				if (response.data) {
					console.log('[Members] Optimistically updating member list', response)

					if (currentMemberId !== null && String(currentMemberId) === targetMemberId) {
						queryClient.setQueryData(
							[API.QUERY_KEY_ADMIN_MEMBER_DETAIL, currentMemberId],
							(oldData: any) => {
								if (!oldData?.data) return oldData
								return {
									...oldData,
									data: { ...oldData.data, ...response.data },
								}
							},
						)
					}

					if (Array.isArray(membersRef.current)) {
						membersRef.current = membersRef.current.map((member: any) => {
							if (String(member.memberId) === targetMemberId) {
								return { ...member, ...response.data }
							}
							return member
						})
					}

					queryClient.setQueriesData(
						{ queryKey: [API.QUERY_KEY_ADMIN_MEMBERS] },
						(oldData: any) => {
							if (!oldData || !oldData.data || !oldData.data.rows) return oldData
							const newRows = oldData.data.rows.map((member: any) => {
								if (String(member.memberId) === targetMemberId) {
									return { ...member, ...response.data }
								}
								return member
							})
							return {
								...oldData,
								data: { ...oldData.data, rows: newRows },
							}
						},
					)
				} else {
					// Fallback if no data provided
					queryClient.invalidateQueries({
						queryKey: [API.QUERY_KEY_ADMIN_MEMBER_DETAIL, response.memberId],
					})
					queryClient.invalidateQueries({
						queryKey: [API.QUERY_KEY_ADMIN_MEMBERS],
					})
				}
			}
		},
		[currentMemberId, queryClient],
	)

	const chatHandler = useCallback(
		(response: { memberId: number; type: string; chat?: any }) => {
			if (
				!membersRef.current?.find(
					(m: { memberId: number }) => m?.memberId === response?.memberId,
				)
			)
				return

			const applyUpdate = (member: any) => {
				if (member.memberId !== response.memberId) return member
				if (response.type === 'read') return { ...member, unreadCount: 0 }
				const isCurrentChat = currentMemberId === response.memberId && isChatModalVisible
				return {
					...member,
					unreadCount: isCurrentChat ? 0 : (member.unreadCount || 0) + 1,
					lastMessageAt: response.chat?.createdAt || new Date().toISOString(),
				}
			}

			membersRef.current = membersRef.current.map(applyUpdate)

			queryClient.setQueriesData(
				{ queryKey: [API.QUERY_KEY_ADMIN_MEMBERS] },
				(oldData: any) => {
					if (!oldData?.data?.rows) return oldData
					return {
						...oldData,
						data: { ...oldData.data, rows: oldData.data.rows.map(applyUpdate) },
					}
				},
			)
		},
		[queryClient, currentMemberId, isChatModalVisible],
	)

	useSSEEvent(API.SSE_MEMBER, memberHandler)
	useSSEEvent(API.SSE_CHAT, chatHandler)

	const handleMemberDelete = (member: any) => {
		const paramData = {
			memberId: member.memberId,
		}

		modal.confirm({
			title: '確認',
			icon: <ExclamationCircleOutlined className='text-red-600' />,
			content: (
				<p>
					<span className='text-red-600'>{`${member.displayName || 'ー'}`}</span>
					様の情報を削除してもよろしいでしょうか？
				</p>
			),
			okText: '削除',
			okButtonProps: {
				size: 'large',
				type: 'primary',
				danger: true,
			},
			cancelText: '閉じる',
			cancelButtonProps: {
				size: 'large',
			},
			centered: true,
			onOk() {
				memberDeleteMutation.mutate(paramData)
				hideMemberDetailModal()
			},
		})
	}

	const handleMembersExport = () => {
		modal.confirm({
			title: '確認',
			content: (
				<>
					<div className='p-4 bg-amber-100 mb-4 rounded'>
						<p className='text-center whitespace-pre-wrap'>{`確認のためにログインパスワードを\n入力してください`}</p>
					</div>
					<Form
						form={membersCSVForm}
						preserve={false}
						layout='vertical'
						initialValues={{
							password: '',
						}}
					>
						<Form.Item
							name='password'
							label='ログインパスワード'
							rules={[
								{
									required: true,
									message: 'パスワードを入力してください',
								},
							]}
						>
							<Input.Password placeholder='パスワードを入力してください' />
						</Form.Item>
					</Form>
					<Divider />
					<p className='font-bold whitespace-pre-wrap text-center'>{`※検索条件に関わらず、すべての顧客情報を\nCSVに出力します`}</p>
				</>
			),
			okText: '確認',
			cancelText: '閉じる',
			centered: true,
			onOk(close) {
				membersCSVForm
					.validateFields()
					.then((values) => {
						const paramData = {
							password: values.password,
							...filters,
						}

						membersExportMutation.mutate(paramData, {
							onSuccess: (data, variables, context) => {
								close()
							},
						})
					})
					.catch((error) => {})
			},
		})
	}

	const handleMemberVisitsExport = () => {
		modal.confirm({
			title: '確認',
			content: (
				<>
					<Form
						form={memberVisitsCSVForm}
						preserve={false}
						layout='vertical'
						initialValues={{
							from: null,
							to: null,
							password: '',
						}}
					>
						<Form.Item
							name='from'
							label='期間（開始）'
							rules={[{ required: true, message: '開始日を入力してください' }]}
						>
							<DatePicker className='w-full' inputReadOnly />
						</Form.Item>
						<Form.Item
							name='to'
							label='期間（終了）'
							rules={[{ required: true, message: '終了日を入力してください' }]}
						>
							<DatePicker className='w-full' inputReadOnly />
						</Form.Item>
						<Form.Item
							name='password'
							label='ログインパスワード'
							rules={[
								{
									required: true,
									message: 'パスワードを入力してください',
								},
							]}
						>
							<Input.Password placeholder='パスワードを入力してください' />
						</Form.Item>
					</Form>
				</>
			),
			okText: '確認',
			cancelText: '閉じる',
			centered: true,
			onOk(close) {
				memberVisitsCSVForm
					.validateFields()
					.then((values) => {
						const fromDate = values.from?.toDate?.() ?? values.from
						const toDate = values.to?.toDate?.() ?? values.to

						if (moment(fromDate).isAfter(moment(toDate), 'day')) {
							message.warning('開始日は終了日以前に設定してください')
							return
						}

						memberVisitsExportMutation.mutate(
							{
								from: moment(fromDate).startOf('day').toISOString(),
								to: moment(toDate).endOf('day').toISOString(),
								password: values.password,
							},
							{
								onSuccess: () => {
									close()
								},
							},
						)
					})
					.catch(() => {})
			},
		})
	}

	const showMemberDetailModal = (memberId: number) => {
		setCurrentMemberId(memberId)
		queryClient.invalidateQueries([API.QUERY_KEY_ADMIN_MEMBER_DETAIL, memberId])
		setIsMemberDetailModalVisible(true)
	}

	const hideMemberDetailModal = () => {
		setIsMemberDetailModalVisible(false)
	}
	const showMemberEditDetailModal = () => {
		setIsMemberDetailModalVisible(false)
		setIsMemberEditDetailModalVisible(true)
	}

	const hideMemberEditDetailModal = () => {
		setIsMemberEditDetailModalVisible(false)
		setIsMemberDetailModalVisible(true)
	}

	const showChatModal = (member: any) => {
		setCurrentMemberId(member.memberId)
		queryClient.invalidateQueries([API.QUERY_KEY_ADMIN_MEMBER_DETAIL, member.memberId])
		setIsChatModalVisible(true)
	}

	const hideChatModal = () => {
		setIsChatModalVisible(false)
	}

	const showMemberRemarksModal = (memberId: number) => {
		setCurrentMemberId(memberId)
		setIsMemberRemarksModalVisible(true)
	}

	const hideMemberRemarksModal = () => {
		setIsMemberRemarksModalVisible(false)
	}

	const commonStartColumns = [
		{
			title: 'ID',
			dataIndex: 'memberId',
			align: 'center',
			width: 50,
			sorter: (a: { memberId: number }, b: { memberId: number }) => a.memberId - b.memberId,
			sortDirections: ['ascend', 'descend'],
			fixed: 'left',
		},
		{
			title: '会員コード',
			dataIndex: 'memberCode',
			align: 'center',
			width: 120,
			sorter: (a: Member, b: Member) => {
				const aCode = Number(a.memberCode) || 0
				const bCode = Number(b.memberCode) || 0
				return aCode - bCode
			},
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: (
				<>
					<span className='whitespace-nowrap'>LINE</span>
					<span className='whitespace-nowrap'>チャット</span>
				</>
			),
			dataIndex: 'member',
			key: 'unreadCount',
			align: 'center',
			width: 90,
			fixed: 'left',
			sorter: (memberA: { unreadCount: number }, memberB: { unreadCount: number }) =>
				memberB.unreadCount - memberA.unreadCount,
			sortDirections: ['ascend', 'descend'],
			render: (member: Member) => (
				<TapAnimationComponent className='inline-block'>
					<Badge
						offset={[-1, 2]}
						style={{ backgroundColor: COMMONS.CUSTOM_GREEN }}
						count={member?.unreadCount || 0}
					>
						<Tooltip title='LINEチャット' placement='top'>
							<Image
								width={50}
								height={50}
								className='rounded-full'
								src={
									member.picUrl
										? member.picUrl.includes('randomuser')
											? member.picUrl
											: `${member.picUrl}/small`
										: ''
								}
								fallback='/no-image.png'
								preview={false}
								onClick={() => showChatModal(member)}
							/>
						</Tooltip>
					</Badge>
				</TapAnimationComponent>
			),
		},
		{
			title: 'LINE名',
			dataIndex: 'displayName',
			align: 'center',
			width: 150,
		},
		// {
		//   title: "氏名",
		//   dataIndex: "fullName",
		//   align: "center",
		//   width: 150,
		//   render: (member) => (
		//     <>
		//       <p>
		//         {member?.lastName || "ー"} {member?.firstName || "ー"}
		//       </p>
		//       <p className="text-xs text-gray-400">
		//         （{member?.lastNameKana || "ー"} {member?.firstNameKana || "ー"}）
		//       </p>
		//     </>
		//   ),
		// },
		// {
		//   title: "電話番号",
		//   dataIndex: "telephone",
		//   align: "center",
		//   width: 120,
		// },
	]

	const commonEndColumns = [
		{
			title: '備考欄',
			dataIndex: 'member',
			align: 'center',
			width: 200,
			render: (member: Member) =>
				member?.remarks ? (
					<div
						style={{
							fontSize: '14px',
							backgroundColor: '#fff7e6',
						}}
						className='m-1 p-2'
					>
						{member?.remarks || ''}
						<TapAnimationComponent className='inline-block'>
							<Button
								size='small'
								onClick={() => showMemberRemarksModal(Number(member?.memberId))}
								className='m-1'
								icon={<EditOutlined />}
							/>
						</TapAnimationComponent>
					</div>
				) : (
					<TapAnimationComponent className='inline-block'>
						<Button
							onClick={() => showMemberRemarksModal(Number(member?.memberId))}
							className='m-1'
							icon={<PlusOutlined />}
						>
							備考追加
						</Button>
					</TapAnimationComponent>
				),
		},
		{
			title: '来館回数',
			dataIndex: 'countVisit',
			align: 'center',
			width: 100,
			sorter: (a: { countVisit: number }, b: { countVisit: number }) =>
				a.countVisit - b.countVisit,
			render: (countVisit: number | string) => <>{countVisit || '0'}回</>,
		},
		{
			title: (
				<>
					<span className='whitespace-nowrap'>最終</span>
					<span className='whitespace-nowrap'>来館日</span>
				</>
			),
			dataIndex: 'lastVisit',
			align: 'center',
			width: 110,
			sorter: (a: Member, b: Member) => COMMONS.compareDateTime(a.lastVisit, b.lastVisit),
			sortDirections: ['ascend', 'descend'],
			render: (lastVisit: Date) => (
				<>{lastVisit ? moment(lastVisit).format('YYYY年M月D日') : 'ー'}</>
			),
		},
		{
			title: 'ポイント',
			dataIndex: 'kakeruPoint',
			align: 'center',
			width: 90,
			sorter: (a: Member, b: Member) => a.kakeruPoint - b.kakeruPoint,
			sortDirections: ['ascend', 'descend'],
			render: (kakeruPoint: number) => <>{kakeruPoint ? kakeruPoint : '0'}</>,
		},

		{
			title: (
				<>
					<span className='whitespace-nowrap'>友だち</span>
					<span className='whitespace-nowrap'>登録日</span>
				</>
			),
			dataIndex: 'createdAt',
			align: 'center',
			width: 110,
			sorter: (a: Member, b: Member) => COMMONS.compareDateTime(a.createdAt, b.createdAt),
			sortDirections: ['ascend', 'descend'],
			render: (createdAt: Date) => (
				<>{createdAt ? moment(createdAt).format('YYYY年M月D日') : 'ー'}</>
			),
		},
		{
			title: (
				<>
					<span className='whitespace-nowrap'>会員</span>
					<span className='whitespace-nowrap'>登録日</span>
				</>
			),
			dataIndex: 'memberSince',
			align: 'center',
			width: 110,
			sorter: (a: Member, b: Member) => COMMONS.compareDateTime(a.memberSince, b.memberSince),
			sortDirections: ['ascend', 'descend'],
			render: (memberSince: Date) => (
				<>{memberSince ? moment(memberSince).format('YYYY年M月D日') : 'ー'}</>
			),
		},
		{
			title: '会員ステータス',
			dataIndex: 'membershipStatus',
			align: 'center',
			width: 120,
			render: (status: string) => {
				const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
					active: {
						label: '本登録',
						bg: '#D1FAE5',
						text: '#065F46',
					},
					provisional: {
						label: '仮登録',
						bg: '#FEF3C7',
						text: '#92400E',
					},
					expired: {
						label: '期限切れ',
						bg: '#FEE2E2',
						text: '#991B1B',
					},
					deactivated: {
						label: '無効',
						bg: '#F3F4F6',
						text: '#6B7280',
					},
				}
				const config = statusConfig[status]
				if (!config) return <>{status || 'ー'}</>
				return (
					<span
						style={{
							display: 'inline-block',
							padding: '4px 12px',
							borderRadius: '9999px',
							fontSize: '12px',
							fontWeight: 500,
							backgroundColor: config.bg,
							color: config.text,
						}}
					>
						{config.label}
					</span>
				)
			},
		},
		{
			title: '有効期限',
			dataIndex: 'activeUntil',
			align: 'center',
			width: 90,
			sorter: (a: Member, b: Member) => COMMONS.compareDateTime(a.activeUntil, b.activeUntil),
			sortDirections: ['ascend', 'descend'],
			render: (activeUntil: Date) => (
				<>{activeUntil ? moment(activeUntil).format('YYYY年M月D日') : 'ー'}</>
			),
		},
		{
			title: '決済方法',
			dataIndex: 'lastPaymentMethod',
			align: 'center',
			width: 120,
			render: (paymentMethod: string) => {
				if (!paymentMethod) return <>ー</>
				const methodConfig: Record<string, { label: string; color: string }> = {
					card: {
						label: 'カード決済',
						color: '#2563EB', // Royal Blue
					},
					on_site: {
						label: '現地決済',
						color: '#7C3AED', // Violet
					},
				}
				const config = methodConfig[paymentMethod]
				if (!config) return <>{paymentMethod}</>
				return (
					<span
						style={{
							display: 'inline-block',
							padding: '4px 10px',
							borderRadius: '4px',
							fontSize: '12px',
							fontWeight: 500,
							backgroundColor: '#FFFFFF',
							color: config.color,
							border: `1px solid ${config.color}`,
						}}
					>
						{config.label}
					</span>
				)
			},
		},
		{
			title: '決済完了日',
			dataIndex: 'lastPaymentDate',
			align: 'center',
			width: 110,
			sorter: (a: any, b: any) =>
				COMMONS.compareDateTime(a.lastPaymentDate, b.lastPaymentDate),
			sortDirections: ['ascend', 'descend'],
			render: (lastPaymentDate: Date) => (
				<>{lastPaymentDate ? moment(lastPaymentDate).format('YYYY年M月D日') : 'ー'}</>
			),
		},
		{
			title: '詳細',
			dataIndex: 'member',
			align: 'center',
			width: 50,
			render: (member: Member) => (
				<>
					<TapAnimationComponent className='inline-block'>
						<Tooltip title='詳細' placement='top'>
							<Button
								size='large'
								className='m-1'
								icon={<EyeOutlined />}
								onClick={() => {
									//@ts-expect-error
									showMemberDetailModal(member?.memberId)
								}}
							/>
						</Tooltip>
					</TapAnimationComponent>
					{/* <Divider type="vertical" />
					<TapAnimationComponent className="inline-block">
						<Tooltip title="削除" placement="top">
							<Button
								size="large"
								className="m-1"
								icon={<DeleteOutlined />}
								danger
								onClick={() => {
									handleMemberDelete(member)
								}}
							/>
						</Tooltip>
					</TapAnimationComponent> */}
				</>
			),
		},
	]

	const commonColumnRender = (value: string) => (
		<p className='text-sm whitespace-pre-wrap'>{value ?? ''}</p>
	)

	const getMemberAttributeColumn = useCallback(
		(memberAttribute: MemberAttribute) => {
			switch (memberAttribute.type) {
				case 'text':
				case 'textarea':
				case 'color':
				case 'url':
				case 'number_integer':
				case 'number_float':
				case 'select':
				case 'radio':
				case 'checkbox':
				case 'email':
				case 'telephone':
				case 'firstName':
				case 'lastName':
				case 'firstNameKana':
				case 'lastNameKana':
				case 'time': {
					return {
						title: memberAttribute.label,
						width: 200,
						dataIndex: `memberAttributeId${memberAttribute.memberAttributeId}`,
						render: commonColumnRender,
						sorter: (a: Member, b: Member) => {
							const key = `memberAttributeId${memberAttribute.memberAttributeId}`
							const aValue = a[key as keyof Member] as string
							const bValue = b[key as keyof Member] as string
							if (aValue === null) return 1 // Move null values to the end
							if (bValue === null) return -1
							return aValue.localeCompare(bValue)
						},
						sortDirections: ['ascend', 'descend'],
					}
				}
				// Combine address fields under the assumption that postal code is required
				case 'address_postal': {
					// case 'address_prefecture':
					// case 'address_city':
					// case 'address_address':
					// case 'address_building':
					return {
						title: memberAttribute.label,
						width: 200,
						dataIndex: `member`, // We need access to the entire member object to get the address parts
						render: (member: Member) => {
							const addressStr = COMMONS.generateMemberAttributeAddressStr({
								member,
								memberAttributes,
								addrGroupSection: memberAttribute.section,
							})

							return commonColumnRender(addressStr)
						},
						sorter: (a: Member, b: Member) => {
							const key = `memberAttributeId${memberAttribute.memberAttributeId}`
							const aValue = a[key as keyof Member] as string
							const bValue = b[key as keyof Member] as string
							if (aValue === null) return 1 // Move null values to the end
							if (bValue === null) return -1
							return aValue.localeCompare(bValue)
						},
						sortDirections: ['ascend', 'descend'],
					}
				}
				case 'date': {
					return {
						title: memberAttribute.label,
						width: 137,
						dataIndex: `memberAttributeId${memberAttribute.memberAttributeId}`,
						render: (value: string) =>
							value
								? moment(value).format('YYYY年M月D日 ')
								: commonColumnRender(value),
						sorter: (a: Member, b: Member) => {
							const key = `memberAttributeId${memberAttribute.memberAttributeId}`
							const aValue = a[key as keyof Member] as Date
							const bValue = b[key as keyof Member] as Date
							if (aValue === null) return 1 // Move null values to the end
							if (bValue === null) return -1
							return COMMONS.compareDateTime(aValue, bValue)
						},
						sortDirections: ['ascend', 'descend'],
					}
				}
				case 'datetime': {
					return {
						title: memberAttribute.label,
						width: 137,
						dataIndex: `memberAttributeId${memberAttribute.memberAttributeId}`,
						render: (value: string) =>
							value
								? moment(value).format('YYYY年M月D日 HH:mm ')
								: commonColumnRender(value),
						sorter: (a: Member, b: Member) => {
							const key = `memberAttributeId${memberAttribute.memberAttributeId}`
							const aValue = a[key as keyof Member] as Date
							const bValue = b[key as keyof Member] as Date
							if (aValue === null) return 1 // Move null values to the end
							if (bValue === null) return -1
							return COMMONS.compareDateTime(aValue, bValue)
						},
						sortDirections: ['ascend', 'descend'],
					}
				}
				case 'image': {
					return {
						title: memberAttribute.label,
						align: 'center',
						width: 130,
						dataIndex: `memberAttributeId${memberAttribute.memberAttributeId}`,
						render: (value: string) => (
							<>
								{value ? (
									<Image
										preview={true}
										className='object-contain max-w-full max-h-full m-auto'
										src={
											API.GET_CUSTOM_REGISTRATION_UPLOAD_PATH(
												memberAttribute.memberAttributeId,
											) + `/${value}`
										}
									/>
								) : (
									<Image
										preview={true}
										className='w-20 h-20'
										src='/no-image.png'
									/>
								)}
							</>
						),
						sorter: (a: Member, b: Member) => {
							const key = `memberAttributeId${memberAttribute.memberAttributeId}`
							if (a[key as keyof Member] === null || a[key as keyof Member] === '')
								return 1 // Move null values to the end
							if (b[key as keyof Member] === null || b[key as keyof Member] === '')
								return -1
							return 0
						},
						sortDirections: ['ascend', 'descend'],
					}
				}
				default:
					return null
			}
		},
		[memberAttributes],
	)

	const memberColumns = [
		...commonStartColumns,
		...memberAttributes.map(getMemberAttributeColumn),
		// ...[
		//   {
		//     title: "メールアドレス",
		//     dataIndex: "email",
		//     align: "center",
		//     width: 200,
		//   },
		//   {
		//     title: "住所",
		//     dataIndex: "address",
		//     align: "center",
		//     width: 150,
		//   },
		// ],
		...commonEndColumns,
	].filter(Boolean)

	const handleTableChange = (
		paginationData: Pagination,
		sorter: { order: string; field: string },
	) => {
		if (paginationData.pageSize !== paginationPerPage) {
			setPaginationPerPage(paginationData.pageSize)
		}

		if (paginationData.current !== paginationPage) {
			setPaginationPage(paginationData.current)
		}

		if (sorter && sorter.order) {
			if (sorter.order === 'ascend') {
				if (sorter.order !== paginationSort) {
					setPaginationSort('asc')
				}
			} else if (sorter.order === 'descend') {
				if (sorter.order !== paginationSort) {
					setPaginationSort('desc')
				}
			}
		}

		if (sorter && sorter.field) {
			if (sorter.field !== paginationSortKey) {
				setPaginationSortKey(sorter.field)
			}
		}
	}

	return (
		<>
			<BaseAnimationComponent>
				<PageHeaderComponent
					publicSettings={publicSettings}
					title={isCampaignPage ? 'キャンペーン応募者一覧' : '顧客管理'}
					actions={<MemberVisit />}
				/>
				<Card bordered={false} className='shadow-none'>
					<Row>
						<Col xs={24}>
							<MembersFilters
								filters={filters}
								setFilters={setFilters}
								setPaginationPage={setPaginationPage}
							/>
						</Col>
						<Divider />
						<MotionColComponent
							xs={24}
							variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
							initial='hidden'
							animate='show'
							exit='hidden'
						>
							<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
								<div className='flex justify-end mb-2'>
									<Button
										size='large'
										icon={<DownloadOutlined />}
										onClick={handleMemberVisitsExport}
										loading={memberVisitsExportMutation.isPending}
										className='mr-2'
									>
										来館履歴CSV
									</Button>
									<Button
										size='large'
										icon={<DownloadOutlined />}
										onClick={handleMembersExport}
										loading={membersExportMutation.isPending}
									>
										お客様リストCSV
									</Button>
								</div>
								<Table
									$publicSettings={publicSettings}
									bordered
									size='small'
									loading={membersQuery.isPending}
									columns={memberColumns}
									// columns={isCampaignPage ? campaignMemberColumns : memberColumns}
									scroll={{
										x: 'max-content',
										y: 'calc(100vh - 320px)',
									}}
									locale={{ emptyText: 'お客様のデータがありません。' }}
									//@ts-expect-error
									onChange={handleTableChange}
									pagination={{
										responsive: true,
										showSizeChanger: true,
										onShowSizeChange: (current, pageSize) =>
											setPaginationPerPage(pageSize),
										current: paginationPage,
										pageSize: paginationPerPage,
										total: paginationTotal,
										showTotal: (total, range) =>
											`全${total}件中${range[0]}～${range[1]}件目`,
										defaultCurrent: 1,
										defaultPageSize: 20,
										position: ['bottomCenter'],
									}}
									dataSource={
										members
											? members.map((member: Member) => {
													return {
														key: member?.memberId,
														member: member,
														...member,
													}
												})
											: []
									}
								/>
							</motion.div>
						</MotionColComponent>
					</Row>
				</Card>
			</BaseAnimationComponent>
			<MemberNoteModalComponent
				isMemberRemarksModalVisible={isMemberRemarksModalVisible}
				hideMemberRemarksModal={hideMemberRemarksModal}
				currentMember={currentMember}
			/>
			<MemberDetailModalComponent
				{...props}
				isMemberDetailModalVisible={isMemberDetailModalVisible}
				hideMemberDetailModal={hideMemberDetailModal}
				currentMember={currentMember}
				memberAttributes={memberAttributes}
				handleMemberDelete={handleMemberDelete}
				showMemberEditDetailModal={showMemberEditDetailModal}
			/>
			<MemberEditDetailModalComponent
				{...props}
				isMemberEditDetailModalVisible={isMemberEditDetailModalVisible}
				hideMemberEditDetailModal={hideMemberEditDetailModal}
				currentMember={currentMember}
				memberAttributes={memberAttributes}
			/>
			<ChatModalComponent
				{...props}
				isChatModalVisible={isChatModalVisible}
				hideChatModal={hideChatModal}
				currentMember={currentMember}
			/>
			{contextHolder}
		</>
	)
}

export default Members
